import { supabase } from "@/lib/supabase";
import {
  CANONICAL_INTERNAL_COMPLIANCE_IDEMPOTENCY_KEY_PATTERN,
  CanonicalInternalComplianceReviewContractError,
  assertCanonicalInternalComplianceReviewCursor,
  assertCanonicalInternalComplianceReviewQueueLimit,
  assertCanonicalInternalComplianceReviewUuid,
  isCanonicalInternalComplianceReviewRequirementKey,
  isCanonicalInternalComplianceReviewState,
  parseCanonicalInternalComplianceReviewDecisionResult,
  parseCanonicalInternalComplianceReviewDetail,
  parseCanonicalInternalComplianceReviewHistory,
  parseCanonicalInternalComplianceReviewQueue,
  validateCanonicalInternalComplianceDecisionInput,
  type CanonicalInternalComplianceReviewDecision,
  type CanonicalInternalComplianceReviewDecisionResult,
  type CanonicalInternalComplianceReviewDetail,
  type CanonicalInternalComplianceReviewHistory,
  type CanonicalInternalComplianceReviewQueue,
  type CanonicalInternalComplianceReviewRequirementKey,
  type CanonicalInternalComplianceReviewState,
} from "@/data/contractor/compliance/canonicalInternalComplianceReview";

export const INDIVIDUAL_COMPLIANCE_OPS_REVIEW_READ_FUNCTION =
  "individual-compliance-ops-review-read";
export const INDIVIDUAL_COMPLIANCE_OPS_REVIEW_DECIDE_FUNCTION =
  "individual-compliance-ops-review-decide";

export type InternalComplianceReviewErrorKind =
  | "invalid_request"
  | "authentication_required"
  | "internal_review_forbidden"
  | "not_found"
  | "stale_decision"
  | "idempotency_conflict"
  | "signing_unavailable"
  | "unexpected_error"
  | "feature_disabled";

export class CanonicalInternalComplianceReviewClientError extends Error {
  constructor(
    public readonly kind: InternalComplianceReviewErrorKind,
    public readonly code: string,
    public readonly status: number | null,
  ) {
    super(code);
    this.name = "CanonicalInternalComplianceReviewClientError";
  }
}

export type InternalComplianceReviewFunctionResult = Readonly<{
  data: unknown;
  error: unknown;
}>;

export type InternalComplianceReviewFunctionInvoker = (
  functionName: string,
  options: Readonly<{ body: Readonly<Record<string, unknown>> }>,
) => Promise<InternalComplianceReviewFunctionResult>;

export type ListInternalComplianceReviewQueueInput = Readonly<{
  organizationId: string;
  contractorId?: string;
  requirementKey?: CanonicalInternalComplianceReviewRequirementKey;
  reviewStatus?: CanonicalInternalComplianceReviewState;
  cursor?: string;
  limit?: number;
}>;

export type GetInternalComplianceReviewDetailInput = Readonly<{
  organizationId: string;
  contractorId?: string;
  targetPersonUserId: string;
}>;

export type GetInternalComplianceReviewHistoryInput = Readonly<{
  organizationId: string;
  contractorId?: string;
  evidenceId: string;
}>;

export type SubmitInternalComplianceReviewDecisionInput = Readonly<{
  organizationId: string;
  contractorId?: string;
  evidenceId: string;
  decision: CanonicalInternalComplianceReviewDecision;
  reasonCode?: string;
  note?: string;
  idempotencyKey: string;
  expectedLatestDecisionId?: string | null;
}>;

export function isCanonicalInternalComplianceReviewEnabled(
  rawValue?: unknown,
): boolean {
  const value = arguments.length === 0
    ? import.meta.env.VITE_CANONICAL_INTERNAL_COMPLIANCE_REVIEW_ENABLED
    : rawValue;
  return value === "true";
}

export function selectInternalComplianceReviewMode(
  rawValue?: unknown,
): "canonical" | "legacy" {
  const enabled = arguments.length === 0
    ? isCanonicalInternalComplianceReviewEnabled()
    : isCanonicalInternalComplianceReviewEnabled(rawValue);
  return enabled
    ? "canonical"
    : "legacy";
}

function defaultInvoker(
  functionName: string,
  options: Readonly<{ body: Readonly<Record<string, unknown>> }>,
): Promise<InternalComplianceReviewFunctionResult> {
  return supabase.functions.invoke(functionName, options);
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function hasOnlyKeys(
  value: Record<string, unknown>,
  allowed: readonly string[],
): boolean {
  const allowedSet = new Set(allowed);
  return Object.keys(value).every((key) => allowedSet.has(key));
}

function assertInputKeys(value: unknown, allowed: readonly string[]): void {
  if (
    !value
    || typeof value !== "object"
    || Array.isArray(value)
    || !hasOnlyKeys(value as Record<string, unknown>, allowed)
  ) {
    throw new CanonicalInternalComplianceReviewClientError(
      "invalid_request",
      "canonical_internal_compliance_review_input_invalid",
      400,
    );
  }
}

function statusFromError(error: unknown): number | null {
  const value = asRecord(error);
  const context = asRecord(value.context);
  const candidate = Number(context.status ?? value.status);
  return Number.isInteger(candidate) && candidate >= 100 && candidate <= 599
    ? candidate
    : null;
}

function codeFromResult(data: unknown, error: unknown): string {
  const response = asRecord(data);
  for (const candidate of [response.code, response.error]) {
    if (
      typeof candidate === "string"
      && (
        /^individual_compliance_ops_review_[a-z0-9_]+$/.test(candidate.trim())
        || candidate.trim() === "internal_compliance_review_permission_forbidden"
      )
    ) {
      return candidate.trim();
    }
  }
  const transport = asRecord(error);
  if (typeof transport.code === "string" && transport.code.trim()) {
    return transport.code.trim();
  }
  if (
    typeof transport.message === "string"
    && (
      /^individual_compliance_ops_review_[a-z0-9_]+$/.test(
        transport.message.trim(),
      )
      || transport.message.trim()
        === "internal_compliance_review_permission_forbidden"
    )
  ) {
    return transport.message.trim();
  }
  return "individual_compliance_ops_review_internal_error";
}

function kindForError(
  code: string,
  status: number | null,
): InternalComplianceReviewErrorKind {
  if (code.includes("stale_decision")) return "stale_decision";
  if (code.includes("idempotency_conflict")) return "idempotency_conflict";
  if (code.includes("signing_unavailable") || status === 503) {
    return "signing_unavailable";
  }
  if (status === 401 || code.includes("unauthorized")) {
    return "authentication_required";
  }
  if (
    status === 403
    || code === "internal_compliance_review_permission_forbidden"
    || code.includes("role_forbidden")
    || code.includes("scope_forbidden")
  ) {
    return "internal_review_forbidden";
  }
  if (
    status === 404
    || code.includes("entity_unavailable")
    || code.includes("preview_unavailable")
    || code.includes("scope_unavailable")
  ) {
    return "not_found";
  }
  if (status === 400 || code.includes("invalid") || code.includes("unsupported")) {
    return "invalid_request";
  }
  return "unexpected_error";
}

export function normalizeInternalComplianceReviewFunctionError(
  data: unknown,
  error: unknown,
): CanonicalInternalComplianceReviewClientError {
  const status = statusFromError(error);
  const code = codeFromResult(data, error);
  return new CanonicalInternalComplianceReviewClientError(
    kindForError(code, status),
    code,
    status,
  );
}

function featureDisabled(): never {
  throw new CanonicalInternalComplianceReviewClientError(
    "feature_disabled",
    "canonical_internal_compliance_review_feature_disabled",
    null,
  );
}

function parseOrNormalize<T>(
  result: InternalComplianceReviewFunctionResult,
  parser: (value: unknown) => T,
): T {
  if (result.error) {
    throw normalizeInternalComplianceReviewFunctionError(
      result.data,
      result.error,
    );
  }
  try {
    return parser(result.data);
  } catch (error) {
    if (error instanceof CanonicalInternalComplianceReviewContractError) {
      throw new CanonicalInternalComplianceReviewClientError(
        "unexpected_error",
        error.code,
        null,
      );
    }
    throw error;
  }
}

function selectedContractorId(value: string | undefined): string | undefined {
  return value === undefined
    ? undefined
    : assertCanonicalInternalComplianceReviewUuid(value);
}

export function createCanonicalInternalComplianceReviewClient(dependencies?: {
  invoke?: InternalComplianceReviewFunctionInvoker;
  enabled?: () => boolean;
}) {
  const invoke = dependencies?.invoke ?? defaultInvoker;
  const enabled =
    dependencies?.enabled ?? isCanonicalInternalComplianceReviewEnabled;

  function assertEnabled(): void {
    if (!enabled()) featureDisabled();
  }

  return Object.freeze({
    async listQueue(
      input: ListInternalComplianceReviewQueueInput,
    ): Promise<CanonicalInternalComplianceReviewQueue> {
      assertEnabled();
      assertInputKeys(input, [
        "organizationId",
        "contractorId",
        "requirementKey",
        "reviewStatus",
        "cursor",
        "limit",
      ]);
      const organizationId =
        assertCanonicalInternalComplianceReviewUuid(input.organizationId);
      const contractorId = selectedContractorId(input.contractorId);
      if (
        input.requirementKey !== undefined
        && !isCanonicalInternalComplianceReviewRequirementKey(
          input.requirementKey,
        )
      ) {
        throw new CanonicalInternalComplianceReviewClientError(
          "invalid_request",
          "canonical_internal_compliance_review_requirement_unsupported",
          400,
        );
      }
      if (
        input.reviewStatus !== undefined
        && !isCanonicalInternalComplianceReviewState(input.reviewStatus)
      ) {
        throw new CanonicalInternalComplianceReviewClientError(
          "invalid_request",
          "canonical_internal_compliance_review_state_unsupported",
          400,
        );
      }
      const cursor = input.cursor === undefined
        ? undefined
        : assertCanonicalInternalComplianceReviewCursor(input.cursor);
      const limit = input.limit === undefined
        ? undefined
        : assertCanonicalInternalComplianceReviewQueueLimit(input.limit);
      const body = Object.freeze({
        operation: "list_queue",
        organizationId,
        ...(contractorId ? { contractorId } : {}),
        ...(input.requirementKey ? { requirementKey: input.requirementKey } : {}),
        ...(input.reviewStatus ? { reviewStatus: input.reviewStatus } : {}),
        ...(cursor ? { cursor } : {}),
        ...(limit !== undefined ? { limit } : {}),
      });
      return parseOrNormalize(
        await invoke(INDIVIDUAL_COMPLIANCE_OPS_REVIEW_READ_FUNCTION, { body }),
        parseCanonicalInternalComplianceReviewQueue,
      );
    },

    async getDetail(
      input: GetInternalComplianceReviewDetailInput,
    ): Promise<CanonicalInternalComplianceReviewDetail> {
      assertEnabled();
      assertInputKeys(input, [
        "organizationId",
        "contractorId",
        "targetPersonUserId",
      ]);
      const contractorId = selectedContractorId(input.contractorId);
      const body = Object.freeze({
        operation: "get_detail",
        organizationId:
          assertCanonicalInternalComplianceReviewUuid(input.organizationId),
        ...(contractorId ? { contractorId } : {}),
        targetPersonUserId:
          assertCanonicalInternalComplianceReviewUuid(input.targetPersonUserId),
      });
      return parseOrNormalize(
        await invoke(INDIVIDUAL_COMPLIANCE_OPS_REVIEW_READ_FUNCTION, { body }),
        parseCanonicalInternalComplianceReviewDetail,
      );
    },

    async getHistory(
      input: GetInternalComplianceReviewHistoryInput,
    ): Promise<CanonicalInternalComplianceReviewHistory> {
      assertEnabled();
      assertInputKeys(input, [
        "organizationId",
        "contractorId",
        "evidenceId",
      ]);
      const contractorId = selectedContractorId(input.contractorId);
      const body = Object.freeze({
        operation: "get_history",
        organizationId:
          assertCanonicalInternalComplianceReviewUuid(input.organizationId),
        ...(contractorId ? { contractorId } : {}),
        evidenceId:
          assertCanonicalInternalComplianceReviewUuid(input.evidenceId),
      });
      return parseOrNormalize(
        await invoke(INDIVIDUAL_COMPLIANCE_OPS_REVIEW_READ_FUNCTION, { body }),
        parseCanonicalInternalComplianceReviewHistory,
      );
    },

    async submitDecision(
      input: SubmitInternalComplianceReviewDecisionInput,
    ): Promise<CanonicalInternalComplianceReviewDecisionResult> {
      assertEnabled();
      assertInputKeys(input, [
        "organizationId",
        "contractorId",
        "evidenceId",
        "decision",
        "reasonCode",
        "note",
        "idempotencyKey",
        "expectedLatestDecisionId",
      ]);
      if (
        typeof input.idempotencyKey !== "string"
        || !CANONICAL_INTERNAL_COMPLIANCE_IDEMPOTENCY_KEY_PATTERN.test(
          input.idempotencyKey,
        )
      ) {
        throw new CanonicalInternalComplianceReviewClientError(
          "invalid_request",
          "canonical_internal_compliance_review_idempotency_key_invalid",
          400,
        );
      }
      let normalized: ReturnType<
        typeof validateCanonicalInternalComplianceDecisionInput
      >;
      try {
        normalized = validateCanonicalInternalComplianceDecisionInput(input);
      } catch (error) {
        if (error instanceof CanonicalInternalComplianceReviewContractError) {
          throw new CanonicalInternalComplianceReviewClientError(
            "invalid_request",
            error.code,
            400,
          );
        }
        throw error;
      }
      const contractorId = selectedContractorId(input.contractorId);
      const expectedLatestDecisionId =
        input.expectedLatestDecisionId === undefined
          ? undefined
          : input.expectedLatestDecisionId === null
          ? null
          : assertCanonicalInternalComplianceReviewUuid(
              input.expectedLatestDecisionId,
            );
      const body = Object.freeze({
        organizationId:
          assertCanonicalInternalComplianceReviewUuid(input.organizationId),
        ...(contractorId ? { contractorId } : {}),
        evidenceId:
          assertCanonicalInternalComplianceReviewUuid(input.evidenceId),
        decision: normalized.decision,
        ...(normalized.reasonCode ? { reasonCode: normalized.reasonCode } : {}),
        ...(normalized.note ? { note: normalized.note } : {}),
        idempotencyKey: input.idempotencyKey,
        ...(expectedLatestDecisionId !== undefined
          ? { expectedLatestDecisionId }
          : {}),
      });
      return parseOrNormalize(
        await invoke(INDIVIDUAL_COMPLIANCE_OPS_REVIEW_DECIDE_FUNCTION, { body }),
        parseCanonicalInternalComplianceReviewDecisionResult,
      );
    },
  });
}
